import { ApiResult } from '../../../../api/types';
import { useTranslation } from '../../../../hooks';
import { toDisplayTime, toTime } from '../../../../utils';
import { GetAllRes } from '../types/GetAllRes';
import { TableColumnKey, TableRow } from '../types/Table';
import { getAppSourceDisplayValue } from 'src/helper/displayValue/getAppSourceDisplayValue';
import { getSupportedSystemDisplayValue } from 'src/helper/displayValue/getSupportedSystemDisplayValue';
import { getReferralCodeStatusDisplayValue } from 'src/helper/displayValue/getReferralCodeStatusDisplayValue';
import { EnumSupportedSystem } from '@wallet-manager/pfh-pmp-node-def-types/dist/src/DbModel/SwapAgent';
import { getIdvMethodDisplayValue } from 'src/helper/displayValue/getIdvMethodDisplayValue';
import en from 'src/components/i18nProvider/en';
import { addHours, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

interface UseTableRowsProps {
  apiList: ApiResult<GetAllRes>;
}

type ConvertToTableRowsType = (apiList: ApiResult<GetAllRes>) => TableRow[];

const useTableRows = (props: UseTableRowsProps) => {
  const { apiList } = props;

  const { t, translate } = useTranslation('referralCode');

  const convertToTableRows: ConvertToTableRowsType = (apiList) => {
    if (!apiList || !apiList?.rows) return [];

    const tableRows = apiList?.rows.map((row, index): TableRow => {
      const statusDisplayValue = getReferralCodeStatusDisplayValue(row.status, translate);
      const appSourceDisplayValue = getAppSourceDisplayValue(
        row.setting?.[EnumSupportedSystem.Pmp]?.source,
        translate
      );
      const idvMethodDisplayValue = getIdvMethodDisplayValue(
        row.setting?.[EnumSupportedSystem.Pmp]?.kycIdvMethod,
        translate
      );

      const consumedSupportedSystemDisplayValue = getSupportedSystemDisplayValue(
        row.supportedSystem,
        translate
      );

      const getLevelDisplayValue = (field: 'vipLevel' | 'kycLevel') => {
        if (row.supportedSystem === EnumSupportedSystem.Pmp) {
          return '';
        }

        return row.setting?.[EnumSupportedSystem.AbccWallet]?.[field] ?? '';
      };

      const toHongKongDate = (isoTimeStr: string | null) => {
        const time = toTime(isoTimeStr);

        const HONG_KONG_TIME_DIFF = 8; // Hong Kong is UTC+8

        if (!time) return '';

        const offsetTime = addHours(time, HONG_KONG_TIME_DIFF);

        const hongKongTime = utcToZonedTime(offsetTime, 'UTC');

        return format(hongKongTime, 'yyyy-MM-dd');
      };

      return {
        id: index,
        [TableColumnKey.ProgramName]: row.programName,
        [TableColumnKey.CodeReferenceName]: row.name,
        [TableColumnKey.ReferralCode]: row.referralCode,
        [TableColumnKey.DistributorAgentId]: row.distributorAgentId,
        [TableColumnKey.NumberOfUses]: row.currentUseCount,
        [TableColumnKey.MaximumNumberOfUses]: row.maxUseCount ?? en.referralCode.unlimited,
        [TableColumnKey.StartDate]: toHongKongDate(row.startTime),
        [TableColumnKey.EndDate]: toHongKongDate(row.expirationTime),
        [TableColumnKey.ReferralCodeStatus]: statusDisplayValue,
        [TableColumnKey.ConsumedSupportedSystem]: consumedSupportedSystemDisplayValue,
        [TableColumnKey.Source]: appSourceDisplayValue,

        [TableColumnKey.KycLevel]: getLevelDisplayValue('kycLevel'),
        [TableColumnKey.VipLevel]: getLevelDisplayValue('vipLevel'),
        [TableColumnKey.IdvMethod]: idvMethodDisplayValue,

        [TableColumnKey.CreationTime]: toDisplayTime(row.createdDate),
        [TableColumnKey.LastModifiedTime]: toDisplayTime(row.lastModifiedDate),

        rawData: row,
      };
    });

    return tableRows;
  };

  const rows = convertToTableRows(apiList);

  return { rows, totalCounts: apiList.count, convertToTableRows };
};

export default useTableRows;
